'use client';

import { useIsDesktop } from '@/_hooks/useBreakpoint';
import { Container, Text } from '@chakra-ui/react';
import { IShipper } from '@/_types';
import BrandSingleDesktop from './responsive/desktop';
import BrandSingleMobile from './responsive/mobile';
import {
  useCategoryWithProducts,
  useBrandMenuContext,
} from '@/features/brand-menu';
import type { ICategoryWithProducts } from '@/features/brand-menu/types';
import RestaurantHeader from '../restarant-header';
import { IRedux } from '@/_store/types';
import { useSelector } from 'react-redux';
import { useBrand } from '@/entities/brand/api/query';
import { useBrandStore } from '@/entities/brand/model/use-brand-store';
import { useEffect } from 'react';
import useDelivery from '@/app/[locale]/(home)/_components/Header/_hooks/useDelivery';
import { useZoneError } from '../../../_checkout/_hooks/useZoneError';
import { BrandSingleSkeleton } from '../Skeletons/BrandSingleSkeleton';
import { useSearchParams } from 'next/navigation';

interface BrandSingleProps {
  shipper: IShipper | null;
  initialCategories?: ICategoryWithProducts[];
}

const BrandSingle: React.FC<BrandSingleProps> = ({
  shipper,
  initialCategories,
}) => {
  const isDesktop = useIsDesktop();

  const { points } = useSelector((state: IRedux) => state.common);
  const { effectiveMenuId, deliveryType } = useBrandMenuContext(
    shipper?.id || '',
  );

  const searchParams = useSearchParams();
  const tagIds = searchParams.get('tag_ids');
  const sort = searchParams.get('sort');

  const hasFilters = Boolean(tagIds || sort);
  // SSR categories were fetched without menu_id — only use them before branch menu is known.
  const shouldUseInitialData = !hasFilters && !effectiveMenuId;

  const {
    data: categories = [],
    isLoading,
    isError,
  } = useCategoryWithProducts({
    menuId: effectiveMenuId,
    shipperId: shipper?.id || '',
    enabled: Boolean(shipper?.id),
    tag_ids: tagIds || undefined,
    sort: sort || undefined,
    deliveryType,
    initialData: shouldUseInitialData ? initialCategories : undefined,
  });

  const activePoint = points?.find((point) => point.isActive);
  const { data } = useBrand(shipper?.id, {
    lat: activePoint?.location?.[0],
    long: activePoint?.location?.[1],
  });

  useDelivery({ enabled: true });
  const zoneError = useZoneError({ shipperId: shipper?.id || '' });

  const setBrand = useBrandStore((state) => state.setBrand);

  useEffect(() => {
    if (data) {
      setBrand(data);
    }
  }, [data, setBrand]);

  if (!shipper?.id) {
    return (
      <div>
        <RestaurantHeader brand={shipper} />
        <Container mt={4}>
          <Text>Brand not found</Text>
        </Container>
      </div>
    );
  }

  // Skip skeleton when we already have SSR categories
  if (isLoading && !categories.length) {
    return <BrandSingleSkeleton />;
  }

  if (isError && !categories.length) {
    return (
      <div>
        <RestaurantHeader brand={shipper} />
        <Container mt={4}>
          <Text color='red.500'>Error loading menu data</Text>
        </Container>
      </div>
    );
  }

  if (isDesktop) {
    return (
      <BrandSingleDesktop
        brand={shipper}
        zoneError={zoneError}
        brandLogo={shipper?.logo || null}
        categories={categories}
      />
    );
  }

  return (
    <BrandSingleMobile
      brand={shipper}
      zoneError={zoneError}
      categories={categories}
    />
  );
};

export default BrandSingle;
